import React from "react";

import { AlertV4, ButtonV4, ChipV4, ModalV4, SelectV4 } from "../common/ui";

import {
  PE_DEFS,
  PE_OPTIONS,
  PE_TEMPLATE_OPTIONS,
  peTemplateValues,
  searchIcd10,
  timeOfIso,
  timeOfNow,
  type IcdEntry,
  type MockPatient,
  type SummaryChip,
} from "./mockPatientV4";
import {
  DiagnosisApprovalInitial,
  SensitiveNoteRecordInitial,
  hasHistoryContent,
  icdHitsForSearch,
  isIcdCodeUsed,
  screeningSummaryChips,
  screeningSummaryTitle,
  answeredTemplateRows,
  isNormalResult,
  templateRowOptions,
  DiagnosisRowInitial,
  DIAGNOSIS_TYPE_PRIMARY,
  DIAGNOSIS_TYPE_SECONDARY,
  diagnosisFromIcdHit,
  diagnosisSignature,
  keepUnsaved,
  mergeUnsavedMap,
  selectedTemplateRows,
  errorTextOf,
  isAbnormalRow,
  needsSupervisorApproval,
  normalizeExamV4Slice,
  type DiagnosisRow,
  type ExamIds,
  type ExamRow,
  type ExamV4Slice,
  type IcdHit,
  type RecordSaveMode,
  type Subjective,
} from "./examModelV4";
import { CARD_NAMES, isCallInStatus } from "./examDispatchV4";
/* practice ของทีม: card import ชื่อ sequence + ACTIONS จากไฟล์ sequence
   (ดู patient/CardOrdersV4.tsx:8-15) แต่ card เรียก API เองไม่ได้ */
import {
  ACTIONS,
  SEQUENCE_NAME,
  type ExamRunParams,
  type ExamRunParamsLoose,
  type ExamRunSequenceProp,
} from "../common/sequence/ExamV4";

/**
 * หน้าตรวจรักษาตามแบบ PatientDoctor.dc.html (6 · ตรวจรักษา)
 *
 * มี 2 mode ใน component เดียว
 *   mock : ไม่ส่ง `exam` เข้ามา -> ใช้ PE_DEFS + MOCK_ICD10 เหมือนแบบ (visual harness ใช้ mode นี้)
 *   live : MainHISV4 ส่ง slice ของ ExamV4 sequence + ids เข้ามา
 *          อ่าน/บันทึกผ่าน API ชุดเดียวกับ V3 (patient_screen, medical_record/{emr}/diagnosis, icd10KeyUp)
 */

export type DxSelected = { code: string; term: string };

export type CardDoctorV4Props = {
  patient?: MockPatient;
  doctorName?: string;
  /** id ของ user ที่ login อยู่ (ใช้ gate อนุมัติวินิจฉัยแบบ V3: student_user === user id) */
  currentUserId?: number;
  openedAt?: string;
  /**
   * design มี onTpl/tplOpts สำหรับเติมตรวจร่างกายทั้งชุด แต่หน้า PatientDoctor ที่ export มา
   * ไม่ได้แสดง control นี้ จึงปิดไว้ default เพื่อไม่ให้ layout ต่างจากแบบ
   */
  showPeTemplate?: boolean;
  onSelectMenu: (menu: "screen" | "gallery" | "nursing") => void;
  onBackToQueue: () => void;
  onConfirm?: (diagnosis: DxSelected) => void;
  /** slice ของ ExamV4 sequence เมื่อโหลดข้อมูลจริงแล้ว (live mode) */
  exam?: Partial<ExamV4Slice> | null;
  ids?: ExamIds;
  /** ตาม practice ของทีม: card เรียก sequence ของ MainHIS (ดู CardOrdersV4.tsx:29-31) */
  runSequence?: ExamRunSequenceProp;
  /** loading ต่อ action: key "<card>_<ACTION>" (BLC_STATUS ของ CommonInterface) */
  buttonLoadCheck?: Record<string, any>;
  /** error ของ sequenceนี้: key = ชื่อ card (SetErrorMessage ของ CommonInterface) */
  errorMessage?: Record<string, any>;
  /** ปิดข้อความ error: เขียนค่า new ลง state path (ใช้ร่วมกับ AlertV4 onClose) */
  setProp?: (key: string, value: unknown) => void;
  /** encounter.status จาก API (IN_QUEUE / IN_EXAM / ...) ใช้ตัดสินใจว่าต้องเรียกผู้ป่วยเข้าตรวจหรือยัง */
  encounterStatus?: string;
  encounterStatusName?: string;
  /** บทบาทอื่น (พยาบาล/ทะเบียน) ไม่ควรเรียกผู้ป่วยเข้าตรวจแทนแพทย์ */
  canCallInExam?: boolean;
};

type PeRowView = {
  key: string;
  label: string;
  options: string[];
  /** ชื่อที่แสดงของ option (template ใช้ภาษาไทย: เลือกผล/ปกติ/ผิดปกติ/ไม่ได้ตรวจ แม้ master เก็บเป็น Normal/Abnormal) */
  labels?: Record<string, string>;
  placeholder: string;
}

type PeRowControlsArgs = {
  row: PeRowView;
  value: string;
  text: string;
  warn: boolean;
  onSelect: (key: string, value: string) => void;
  onText: (key: string, text: string) => void;
};

/** แถวตรวจร่างกาย 1 แถว (select ระบบ + รายละเอียด) ใช้ทั้ง grid หลักและแถวจาก template */
const PeRowControls: React.FC<PeRowControlsArgs> = ({
  row,
  value,
  text,
  warn,
  onSelect,
  onText,
}) => (
  <div className="hv4-pe-row">
    <label className="hv4-pe-label" htmlFor={`pe_${row.key}`}>
      {row.label}
    </label>
    <SelectV4
      id={`pe_${row.key}`}
      className={warn ? "hv4-pe-warn" : undefined}
      options={row.options.map((option) => ({ value: option, text: row.labels?.[option] ?? option }))}
      value={value === "" ? "-" : value}
      onChange={(next) => onSelect(row.key, next)}
    />
    <input
      className="hv4-input"
      aria-label={`${row.label} รายละเอียด`}
      placeholder={row.placeholder}
      value={text}
      onChange={(e) => onText(row.key, e.target.value)}
    />
  </div>
);

const SECTION_LINKS: { id: string; label: string }[] = [
  { id: "sec-hx", label: "ประวัติ" },
  { id: "sec-pe", label: "ตรวจร่างกาย" },
  { id: "sec-dx", label: "วินิจฉัย" },
];

const emptyPe = (): Record<string, string> =>
  PE_DEFS.reduce((acc, def) => ({ ...acc, [def.key]: "" }), {} as Record<string, string>);

/** IcdEntry (mock) และ IcdHit (API) แสดงด้วย view เดียวกัน */
const entryOf = (item: IcdHit): IcdEntry => ({
  code: item.code,
  term: item.medTerm !== "" ? item.medTerm : item.term,
});

const hitOfEntry = (entry: IcdEntry): IcdHit => ({
  icd10Id: null,
  code: entry.code,
  term: entry.term,
  medTerm: "",
  hasSublevel: false,
});

export const CardDoctorV4: React.FC<CardDoctorV4Props> = ({
  patient,
  doctorName = "พญ. ธิดารัตน์ วัฒนา",
  currentUserId,
  openedAt = "10:48",
  showPeTemplate = false,
  onSelectMenu,
  onBackToQueue,
  onConfirm,
  exam,
  ids,
  runSequence,
  buttonLoadCheck,
  errorMessage,
  setProp,
  encounterStatus,
  encounterStatusName,
  canCallInExam = true,
}) => {
  /* RunSequence ล้าง slice เหลือแค่ {sequenceIndex:"START"} ชั่วครู่ก่อน START handler เขียนทับ
     (MainHISInterface.ts:700-712) จึง normalize ก่อนอ่านทุกครั้ง */
  const examSlice = exam === undefined || exam === null ? null : normalizeExamV4Slice(exam);
  const sliceIds = examSlice?.ids ?? ids ?? {};
  /** ผูก slice กับหน้าจริง (MainHISV4 ผูกให้เสมอเมื่อเป็น HIS V4) */
  const attached = examSlice !== null;
  /** โหลดข้อมูลของ emr สำเร็จแล้ว ใช้ payload/การค้นหาผ่าน API ได้ */
  const live = !!examSlice && !!sliceIds.emrId;
  const mockMode = !attached;

  const cardName = CARD_NAMES.doctor;
  const dispatch = (params: ExamRunParams): void => {
    runSequence?.(params);
  };
  /** buttonLoadCheck["CardDoctorV4_<ACTION>"] === LOADING */
  const busy = (action: string): boolean =>
    buttonLoadCheck?.[`${cardName}_${action}`] === "LOADING";
  const loadError = (): string | null => errorTextOf(errorMessage?.[cardName]);
  /** flag อนุมัติวินิจฉัยจาก slice (V3 ใช้ isStudentUser + ModAuthen) */
  const diagnosisApproval = examSlice?.diagnosisApproval ?? DiagnosisApprovalInitial;
  const approvalRequired = needsSupervisorApproval(diagnosisApproval, currentUserId);
  const [chiefComplaint, setChiefComplaint] = React.useState(
    mockMode ? "ติดตามอาการหลังการรักษาครั้งก่อน" : "",
  );
  const [presentIllness, setPresentIllness] = React.useState("");
  const [pastIllness, setPastIllness] = React.useState("");
  const [personalHistory, setPersonalHistory] = React.useState("");
  const [familyHistory, setFamilyHistory] = React.useState("");

  /* บันทึกเฉพาะ (sensitive note) — ของ V3 อยู่ใน modal History เดียวกับประวัติ (SubPatientHistory.tsx:287-297) */
  const [sensitiveNote, setSensitiveNote] = React.useState("");
  const [historyOpen, setHistoryOpen] = React.useState(false);

  const [peTemplate, setPeTemplate] = React.useState<string>("-");
  const [pe, setPe] = React.useState<Record<string, string>>(emptyPe);
  const [peText, setPeText] = React.useState<Record<string, string>>(emptyPe);
  const [peOther, setPeOther] = React.useState("");
  /** template ที่เลือกใช้กับ EMR นี้ (V3: dropdown เลือกได้หลายชุด CardPhysicalExam.tsx) */
  const [selectedTemplates, setSelectedTemplates] = React.useState<number[]>([]);

  const [principal, setPrincipal] = React.useState<DiagnosisRow | null>(null);
  const [principalTerm, setPrincipalTerm] = React.useState("");
  const [principalActive, setPrincipalActive] = React.useState(false);
  const [principalCause, setPrincipalCause] = React.useState("");
  const [principalQuery, setPrincipalQuery] = React.useState("");

  const [secondary, setSecondary] = React.useState<DiagnosisRow[]>([]);
  const [secondaryQuery, setSecondaryQuery] = React.useState("");
  /** live mode: slice icdResults ใช้ร่วมกันจึงต้องรู้ว่าช่องไหนกำลังค้นหา */
  const [searchTarget, setSearchTarget] = React.useState<"principal" | "secondary">("principal");
  /**
   * dropdown ปิดเมื่อเลือกแล้ว/ล้าง/กด Esc (design ทำ clearResultP() หลัง pick: ui-v4-design.html)
   * ผลค้นหาจริงเก็บใน slice จึงต้องมี flag นี้ ไม่งั้นแถวลิสต์ค้างหลังเลือก
   */
  const [principalSearchOpen, setPrincipalSearchOpen] = React.useState(false);
  const [secondarySearchOpen, setSecondarySearchOpen] = React.useState(false);
  const principalSearchRef = React.useRef<HTMLDivElement | null>(null);
  const secondarySearchRef = React.useRef<HTMLDivElement | null>(null);
  /**
   * แถวที่ลูกศรขึ้น/ลงชี้อยู่ (-1 = ยังไม่เลือกด้วย keyboard)
   * ArrowDown จาก -1 -> แถวแรก, ArrowUp จาก -1 -> แถวสุดท้าย, Enter -> เลือกแถวนี้
   * (ถ้ายังไม่ขึ้น/ลงเลย Enter จะเลือกแถวแรกของผลค้นหา เหมือน Semantic UI Search ใน HIS V3)
   */
  const [principalActiveIndex, setPrincipalActiveIndex] = React.useState(-1);
  const [secondaryActiveIndex, setSecondaryActiveIndex] = React.useState(-1);

  /**
   * คลิกนอกช่องค้นหา ICD-10 -> ปิด dropdown (ทั้ง Principal และ Secondary)
   * ลง document ใน capture phase เพราะต้องรู้ก่อนที่ click ของบัตรในลิสต์จะทำงาน
   * (คลิกในลิสต์ห้ามปิด ก่อน pickPrincipal/pickSecondary จะได้ไม่หลุด)
   */
  React.useEffect(() => {
    if (!principalSearchOpen && !secondarySearchOpen) {
      return undefined;
    }

    const isInside = (container: HTMLDivElement | null, target: Node | null): boolean =>
      container !== null && target !== null && container.contains(target);

    const handleMouseDown = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (!isInside(principalSearchRef.current, target)) {
        setPrincipalSearchOpen(false);
        setPrincipalActiveIndex(-1);
      }
      if (!isInside(secondarySearchRef.current, target)) {
        setSecondarySearchOpen(false);
        setSecondaryActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleMouseDown, true);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, [principalSearchOpen, secondarySearchOpen]);

  /** เวลาบันทึกของ mock mode (live อ่าน slice.recordSavedAt ที่เขียนหลัง server ตอบแล้ว) */
  const [mockSavedAt, setMockSavedAt] = React.useState<string | null>(null);
  const [confirmed, setConfirmed] = React.useState(false);
  /** requestId ของ SAVE_RECORD ที่รอผล ผลของคำขออื่น (เช่นก่อนสลับหน้า) ไม่นำมาตัดสิน Confirm */
  const pendingSaveRef = React.useRef<string | null>(null);
  /** modal อนุมัติวินิจฉัย (V3 ModAuthen "ยืนยันการตรวจสอบ") */
  const [approvalOpen, setApprovalOpen] = React.useState(false);
  const [approvalCreds, setApprovalCreds] = React.useState({ username: "", password: "" });

  /* แถว Principal รวมค่าที่พิมพ์บน UI (Medical Term / สาเหตุ / Active disease) */
  const principalRow: DiagnosisRow | null = principal
    ? {
        ...principal,
        medicalDescription: principalTerm || principal.medicalDescription,
        detail: principalCause,
        activeDisease: principalActive,
      }
    : null;

  const usedCodes =
    principalRow === null || principalRow.icdCode === "" ? [] : [principalRow.icdCode];
  const secondaryUsed = usedCodes.concat(secondary.map((row) => row.icdCode));

  /* 86627 ข้อ 1.1/2.3: ทั้ง mock และ live ต้องไม่แสดงรหัสที่เลือกไปแล้ว และโชว์ไม่เกิน 6 รายการ
     (API icd10KeyUp ตอบได้หลายร้อยแถว -> ถ้าไม่กรอง/จำกัด ผู้ใช้จะเห็นรหัสซ้ำปนมา) */
  const principalResults: IcdEntry[] = !principalSearchOpen
    ? []
    : live
      ? searchTarget === "principal"
        ? icdHitsForSearch(examSlice?.icdResults ?? [], secondaryUsed).map(entryOf)
        : []
      : searchIcd10(principalQuery, secondaryUsed);
  const secondaryResults: IcdEntry[] = !secondarySearchOpen
    ? []
    : live
      ? searchTarget === "secondary"
        ? icdHitsForSearch(examSlice?.icdResults ?? [], secondaryUsed).map(entryOf)
        : []
      : searchIcd10(secondaryQuery, secondaryUsed);

  /* grid ตรวจร่างกาย: ใช้ master organ จาก API เมื่อมี ถ้าไม่มีใช้ grid ของแบบ */
  const apiRows = examSlice && examSlice.examRows.length > 0 ? examSlice.examRows : null;
  /** หน้าจริงระหว่างโหลด: ไม่ grid mock (ร master organ จาก API ก่อน) */
  const showMockGrid = mockMode;

  const peRows: PeRowView[] = apiRows
    ? apiRows.map((row) => ({
        key: row.key,
        label: row.organName,
        options: ["-", ...row.options],
        placeholder: "ระบุรายละเอียดเพิ่มเติม",
      }))
    : showMockGrid
      ? PE_DEFS.map((def) => ({
          key: def.key as string,
          label: def.label,
          options: ["-", ...(PE_OPTIONS as readonly string[])],
          placeholder: def.placeholder,
        }))
      : [];

  /* แถวตรวจร่างกายจาก template ที่ผู้ป่วย/ทีมเคยเลือก (PhysicalExamTemplateList)
     design เดิมไม่มี control นี้ในหน้าที่ export จึงแสดงเฉพาะเมื่อ API มีข้อมูลจริง */
  const apiTemplateRows = examSlice ? examSlice.templateRows : [];
  const templateGroups: { id: number; name: string; rows: ExamRow[] }[] = [];
  for (const row of apiTemplateRows) {
    if (row.templateId === null || row.templateId === undefined) continue;
    const group = templateGroups.find((item) => item.id === row.templateId);
    if (group) group.rows.push(row);
    else {
      templateGroups.push({
        id: row.templateId,
        name: row.groupName !== "" ? row.groupName : "Template",
        rows: [row],
      });
    }
  }

  const toggleTemplate = (templateId: number): void =>
    setSelectedTemplates((current) =>
      current.includes(templateId)
        ? current.filter((id) => id !== templateId)
        : current.concat(templateId),
    );

  /** 86625 ข้อ 4.3: หัวกล่อง template กดย่อ/ขยายได้ (▼/▶) */
  const [collapsedTemplates, setCollapsedTemplates] = React.useState<number[]>([]);
  const toggleTemplateCollapsed = (templateId: number): void =>
    setCollapsedTemplates((current) =>
      current.includes(templateId)
        ? current.filter((id) => id !== templateId)
        : current.concat(templateId),
    );

  const selectedTemplateGroups = templateGroups.filter((group) => selectedTemplates.includes(group.id));
  const unselectedTemplateGroups = templateGroups.filter((group) => !selectedTemplates.includes(group.id));

  /** view ของแถวใน template: dropdown = เลือกผล / ปกติ / ผิดปกติ / ไม่ได้ตรวจ */
  const templateRowView = (row: ExamRow): PeRowView => {
    const options = templateRowOptions(row);
    return {
      key: row.key,
      label: row.organName,
      options: options.map((option) => option.value),
      labels: options.reduce((acc, option) => ({ ...acc, [option.value]: option.text }), {} as Record<string, string>),
      placeholder: "ระบุรายละเอียดที่ผิดปกติ",
    };
  };

  const abnormalOf = (row: PeRowView, source: ExamRow[] | null = apiRows): boolean => {
    const value = pe[row.key] ?? "";
    if (!source) return value === "ผิดปกติ";
    const apiRow = source.find((item) => item.key === row.key);
    return apiRow ? isAbnormalRow({ ...apiRow, result: value }) : false;
  };

  /* 86625 ข้อ 5: กรอกผล (ปกติหรือผิดปกติ) อย่างน้อย 1 รายการ = ครบเงื่อนไข
     "ไม่ได้ตรวจ" (N/A) ไม่นับว่าตรวจ */
  const answeredCount =
    answeredTemplateRows(apiTemplateRows, pe) +
    answeredTemplateRows(apiRows ?? [], pe) +
    Object.entries(pe).filter(([key, value]) => {
      const known = [...apiTemplateRows, ...(apiRows ?? [])].some((row) => row.key === key);
      return !known && value !== "" && value !== "N/A";
    }).length;
  const peDone = answeredCount > 0 || peOther.trim() !== "";
  const hxDone = chiefComplaint.trim() !== "" && presentIllness.trim() !== "";
  /* design นับ Principal ที่เลือกแล้ว (mock ไม่มี icd10 id)
     ส่วน live ตรวจ icd10 จริงตอน save ใน ExamV4 */
  const dxDone = principalRow !== null;

  const missing: string[] = [];
  if (chiefComplaint.trim() === "") missing.push("อาการนำ");
  if (presentIllness.trim() === "") missing.push("Present illness");
  if (!peDone) missing.push("ตรวจร่างกายอย่างน้อย 1 ระบบ");
  if (principalRow === null) missing.push("วินิจฉัย Principal");

  const savingRecord = busy(ACTIONS.SAVE_RECORD);
  const confirmDisabled = missing.length > 0 || confirmed || savingRecord;
  const savedAt = mockMode
    ? mockSavedAt
    : examSlice?.recordSavedAt
      ? timeOfIso(examSlice.recordSavedAt)
      : null;
  const savedText =
    savedAt === null ? "ยังไม่ได้บันทึก" : `บันทึกล่าสุด ${savedAt} โดย ${doctorName}`;

  /* ผู้ป่วยยังอยู่ขั้นรอพบแพทย์: V3 เรียกเข้าตรวจด้วยการ seed patient_illness (assign doctor)
     แล้วให้ backend ย้ายเป็น IN_EXAM ตอนอ่านเวชระเบียน (auto_checkin) */
  const status = encounterStatus ?? "";
  const needsCallIn = live && isCallInStatus(status) && canCallInExam;
  const callingIn = busy(ACTIONS.START_EXAM);

  const handleCallIn = (): void => {
    dispatch({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.START_EXAM,
      card: cardName,
      ...sliceIds,
      currentStatus: status,
    });
  };

  /* การ์ด "สรุปจากการคัดกรอง" อ่านอย่างเดียว + ใช้เกณฑ์ค่าผิดปกติของหน้าคัดกรอง (86623)
     chip: อุณหภูมิ · BP · ชีพจร · O2 Sat · Pain · Triage (เลข + ชื่อระดับ) · ประวัติแพ้ · โรคประจำตัว */
  const summaryChips: SummaryChip[] =
    examSlice && live
      ? screeningSummaryChips({
          vitals: examSlice.vitals,
          triageLevel: examSlice.triage.triageLevel,
          allergyItems: examSlice.allergyItems.filter((item) => item.status !== "CANCEL"),
          problemItems: examSlice.problemItems,
        })
      : (patient?.summary ?? []);

  const summaryTitle = mockMode
    ? `สรุปจากการคัดกรอง (พยาบาลตัวอย่าง 1 · ${openedAt})`
    : examSlice && live
      ? screeningSummaryTitle(examSlice.vitals)
      : "สรุปจากการคัดกรอง";
  const missText =
    missing.length > 0
      ? `ยังขาด: ${missing.join(", ")}`
      : confirmed
        ? "ยืนยันแล้ว"
        : "ครบแล้ว พร้อมยืนยันการตรวจ";

  /*
   * เติมข้อมูลจาก server ลงฟอร์ม
   * เปลี่ยน encounter/EMR: ใช้ค่าของ server ทั้งหมด
   * encounter เดิม (load จบ / บันทึกส่วนอื่น): คงช่องที่ผู้ใช้แก้ค้างไว้ ดู keepUnsaved
   * effect ผูกกับ object ข้อมูลใน slice จึงไม่ทำงานเมื่อบันทึกภาพ/ประวัติแพ้ ซึ่งไม่ได้เปลี่ยนข้อมูลชุดนี้
   */
  const recordIdentity = `${sliceIds.encounterId ?? ""}|${sliceIds.emrId ?? ""}`;
  type ServerForm = {
    identity: string;
    subjective: Subjective;
    otherDetail: string;
    pe: Record<string, string>;
    peText: Record<string, string>;
    diagnosis: { principal: DiagnosisRow | null; secondary: DiagnosisRow[] };
    templates: number[];
  };
  const baselineRef = React.useRef<ServerForm | null>(null);

  React.useEffect(() => {
    if (!examSlice || !sliceIds.emrId) return;

    const sel: Record<string, string> = {};
    const text: Record<string, string> = {};
    for (const row of [...examSlice.examRows, ...examSlice.templateRows]) {
      sel[row.key] = row.result;
      text[row.key] = row.description;
    }
    const server: ServerForm = {
      identity: recordIdentity,
      subjective: examSlice.subjective,
      otherDetail: examSlice.other.otherDetail,
      pe: sel,
      peText: text,
      diagnosis: examSlice.diagnosis,
      templates: examSlice.selectedTemplateIds,
    };
    const previous = baselineRef.current;
    const base = previous !== null && previous.identity === recordIdentity ? previous : null;
    baselineRef.current = server;

    const field = (form: string, pick: (source: ServerForm) => string): string =>
      base === null ? pick(server) : keepUnsaved(form, pick(base), pick(server));

    setChiefComplaint((form) => field(form, (source) => source.subjective.chiefComplaint));
    setPresentIllness((form) => field(form, (source) => source.subjective.presentIllness));
    setPastIllness((form) => field(form, (source) => source.subjective.pastIllness));
    setPersonalHistory((form) => field(form, (source) => source.subjective.personalHistory));
    setFamilyHistory((form) => field(form, (source) => source.subjective.familyHistory));
    setSensitiveNote((form) => field(form, (source) => source.subjective.sensitiveNote));
    setPeOther((form) => field(form, (source) => source.otherDetail));
    setPe((form) => (base === null ? server.pe : mergeUnsavedMap(form, base.pe, server.pe)));
    setPeText((form) => (base === null ? server.peText : mergeUnsavedMap(form, base.peText, server.peText)));
    setSelectedTemplates((form) =>
      base === null || JSON.stringify(form) === JSON.stringify(base.templates) ? server.templates : form,
    );

    /* วินิจฉัยเทียบทั้งชุด: แถวที่บันทึกแล้วได้ pk จาก server จึงเทียบเนื้อหาไม่เทียบ object */
    const formDiagnosis = diagnosisSignature({ principal: principalRow, secondary });
    if (
      base === null ||
      formDiagnosis === diagnosisSignature(base.diagnosis) ||
      formDiagnosis === diagnosisSignature(server.diagnosis)
    ) {
      const savedPrincipal = server.diagnosis.principal;
      setPrincipal(savedPrincipal);
      setPrincipalTerm(savedPrincipal?.medicalDescription ?? "");
      setPrincipalActive(savedPrincipal?.activeDisease ?? false);
      setPrincipalCause(savedPrincipal?.detail ?? "");
      setSecondary(server.diagnosis.secondary);
    }

    /* confirmed เป็นสถานะของ session นี้เท่านั้น (V3 ก็ทำแบบเดียวกัน: CardICD10.tsx completedConfirm)
       record เก่าที่วินิจฉัยไว้แล้วไม่ถือว่าเคยยืนยันในหน้านี้ */
    if (base === null) {
      setConfirmed(false);
      pendingSaveRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    /* อ่านจาก prop ตรง ๆ: normalizeExamV4Slice เติมค่าว่างเป็น array ใหม่ทุก render ถ้าใช้เป็น dep จะวนไม่จบ */
    recordIdentity,
    exam?.subjective,
    exam?.examRows,
    exam?.templateRows,
    exam?.other,
    exam?.diagnosis,
    exam?.selectedTemplateIds,
  ]);

  /* สถานะยืนยันมาจากผลที่ server ตอบครบแล้วเท่านั้น (ล้มบางส่วนจะแสดง error ผ่าน errorMessage) */
  const recordSave = examSlice?.recordSave ?? null;
  React.useEffect(() => {
    if (recordSave === null || recordSave.requestId !== pendingSaveRef.current) return;
    pendingSaveRef.current = null;
    if (recordSave.ok && recordSave.mode === "confirm") {
      setConfirmed(true);
      if (principalRow !== null) {
        onConfirm?.({ code: principalRow.icdCode, term: principalRow.medicalDescription });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordSave]);

  const searchIcd = (target: "principal" | "secondary", value: string): void => {
    setSearchTarget(target);
    const open = value.trim() !== "";
    if (target === "principal") {
      setPrincipalSearchOpen(open);
      setPrincipalActiveIndex(-1);
    } else {
      setSecondarySearchOpen(open);
      setSecondaryActiveIndex(-1);
    }
    if (!live) return;

    const trimmed = value.trim();
    /* พิมพ์รหัส (M54 / I10 / N39.0) ใช้ icdcode พิมพ์คำใช้ medterm เหมือน V3 */
    const method: "medterm" | "icdcode" = /^[A-Za-z]?\d{1,3}(\.\d+)?$|^[A-Z]{1,2}\d{1,3}/.test(
      trimmed,
    )
      ? "icdcode"
      : "medterm";
    dispatch({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SEARCH_ICD10,
      card: cardName,
      ...sliceIds,
      method,
      search: trimmed,
    });
  };

  /**
   * Medical Term ที่ส่งเข้า API คือ medical_description (DPOI.ts:181-192)
   * - design: เติมให้อัตโนมัติเมื่อช่องว่าง
   * - เลือก ICD ใหม่: เทอมเดิมที่ระบบเติมให้ต้องเปลี่ยนตาม hit ถ้าแพทย์พิมพ์เองให้คงไว้ (V3 ทำ vậy: CardICD10.tsx:174-186)
   */
  const termOfHit = (hit: IcdHit, entry: IcdEntry): string =>
    hit.medTerm !== "" ? hit.medTerm : entry.term.split(" (")[0];

  const pickPrincipal = (entry: IcdEntry): void => {
    /* 86627 ข้อ 2.3: ห้ามเลือกรหัสซ้ำกับ Principal/Secondary ที่มีอยู่ */
    if (isIcdCodeUsed(entry.code, secondaryUsed)) return;

    const hit = live
      ? (examSlice?.icdResults?.find((item) => item.code === entry.code) ?? hitOfEntry(entry))
      : hitOfEntry(entry);

    setPrincipal(
      diagnosisFromIcdHit(hit, {
        emrId: sliceIds.emrId,
        type: DIAGNOSIS_TYPE_PRIMARY,
        /* เปลี่ยน ICD ต้องแก้แถวเดิม (V3 CardICD10 principalPK) ถ้าไม่มี pk BE จะสร้าง primary แถวใหม่
           แต่ยังถือแถว id ต่ำสุดเป็น Principal แถวใหม่จึงไปโผล่ใน Secondary (core MedicalRecord.principal_diagnosis) */
        base: {
          ...DiagnosisRowInitial,
          pk: principal?.pk ?? examSlice?.diagnosis.principal?.pk ?? null,
          emr: sliceIds.emrId ?? null,
        },
      }),
    );
    setPrincipalQuery("");
    setPrincipalSearchOpen(false);
    setPrincipalActiveIndex(-1);

    const previousDescription = principal?.medicalDescription ?? "";
    const nextTerm = termOfHit(hit, entry);
    setPrincipalTerm((current) =>
      current === "" || current === previousDescription ? nextTerm : current,
    );
  };

  const pickSecondary = (entry: IcdEntry): void => {
    /* 86627 ข้อ 2.3: รหัสซ้ำกับ Principal หรือ Secondary ที่มีอยู่เพิ่มไม่ได้ */
    if (isIcdCodeUsed(entry.code, secondaryUsed)) return;

    const hit = live
      ? (examSlice?.icdResults?.find((item) => item.code === entry.code) ?? hitOfEntry(entry))
      : hitOfEntry(entry);

    setSecondary((current) =>
      current.concat(
        diagnosisFromIcdHit(hit, {
          emrId: sliceIds.emrId,
          type: DIAGNOSIS_TYPE_SECONDARY,
          base: {
            ...DiagnosisRowInitial,
            type: DIAGNOSIS_TYPE_SECONDARY,
            emr: sliceIds.emrId ?? null,
          },
        }),
      ),
    );
    setSecondaryQuery("");
    setSecondarySearchOpen(false);
    setSecondaryActiveIndex(-1);
  };

  const removeSecondary = (code: string): void =>
    setSecondary((current) => current.filter((row) => row.icdCode !== code));

  const clearPrincipal = (): void => {
    setPrincipal(null);
    setPrincipalTerm("");
    setPrincipalActive(false);
    setPrincipalCause("");
    setPrincipalQuery("");
    setPrincipalSearchOpen(false);
    setPrincipalActiveIndex(-1);
  };

  /* ผลค้นหา ICD-10 จาก API ยาวเป็นสิบแถวได้ แถวที่ลูกศรชี้ต้องอยู่ในสายตา */
  React.useEffect(() => {
    [principalSearchRef.current, secondarySearchRef.current].forEach((container) => {
      const active = container?.querySelector<HTMLElement>(".hv4-icd-active");
      if (active !== null && active !== undefined && typeof active.scrollIntoView === "function") {
        active.scrollIntoView({ block: "nearest" });
      }
    });
  }, [principalActiveIndex, secondaryActiveIndex]);

  type IcdTarget = "principal" | "secondary";

  /** ArrowDown / ArrowUp / Enter / Escape ในช่องค้นหา ICD-10 */
  const handleIcdKeyDown = (
    target: IcdTarget,
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    const results = target === "principal" ? principalResults : secondaryResults;
    const activeIndex = target === "principal" ? principalActiveIndex : secondaryActiveIndex;

    const setOpen = target === "principal" ? setPrincipalSearchOpen : setSecondarySearchOpen;
    const setActiveIndex =
      target === "principal" ? setPrincipalActiveIndex : setSecondaryActiveIndex;

    if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (results.length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(activeIndex < 0 ? 0 : Math.min(activeIndex + 1, results.length - 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(activeIndex < 0 ? results.length - 1 : Math.max(activeIndex - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const index = activeIndex >= 0 && activeIndex < results.length ? activeIndex : 0;
      if (target === "principal") {
        pickPrincipal(results[index]);
      } else {
        pickSecondary(results[index]);
      }
    }
  };

  const handlePeSelect = (key: string, value: string): void => {
    const picked = value === "-" ? "" : value;
    setPe((current) => ({ ...current, [key]: picked }));
    /* 86625 ข้อ 2: เลือก "ปกติ" และช่องรายละเอียดว่าง ให้เติมคำว่า "ปกติ" ให้อัตโนมัติ (ไม่ทับข้อความเดิม)
       master ของบางหน่วยงานเก็บ option เป็นภาษาอังกฤษ (Normal) ถือว่าเป็น "ปกติ" ด้วย */
    setPeText((current) =>
      isNormalResult(picked) && current[key] === "" ? { ...current, [key]: "ปกติ" } : current,
    );
  };

  /**
   * คลิก pill ของหัวข้อหน้า: scroll เองแทนที่จะปล่อยให้ browser ไปที่ #id
   * เพราะ .hv4-sec-nav เป็น sticky อยู่เหนือ content ของ .hv4-main
   * ถ้าใช้ anchor อย่างเดียวหัวข้อของ section (เช่น "3 · วินิจฉัย (Diagnosis) *") จะโดน bar บัง
   * แถม hash ของ URL เปลี่ยนโดยไม่จำเป็น
   */
  const scrollToSection = (event: React.MouseEvent<HTMLAnchorElement>, id: string): void => {
    const link = event.currentTarget;
    const scroller = link.closest(".hv4-main") as HTMLElement | null;
    const target = document.getElementById(id);
    if (!scroller || !target || typeof scroller.scrollTo !== "function") return;

    event.preventDefault();

    const nav = scroller.querySelector<HTMLElement>(".hv4-sec-nav");
    const scrollerTop = scroller.getBoundingClientRect().top;
    const navOverlap = nav ? nav.getBoundingClientRect().bottom - scrollerTop + 8 : 0;
    const delta = target.getBoundingClientRect().top - scrollerTop - navOverlap;

    scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: "smooth" });
  };

  const handlePeText = (key: string, value: string): void =>
    setPeText((current) => ({ ...current, [key]: value }));

  const handlePeTemplate = (option: string): void => {
    const values = peTemplateValues(option);
    setPe({ ...values.sel });
    setPeText({ ...values.text });
    setPeTemplate(option === "ล้างค่า" ? "-" : option);
  };

  const subjectivePayload = (): Subjective => ({
    ...(examSlice?.subjective ?? {
      illnessId: null,
      historyId: null,
      chiefComplaint: "",
      presentIllness: "",
      pastIllness: "",
      personalHistory: "",
      familyHistory: "",
      sensitiveNote: "",
      advice: "",
    }),
    chiefComplaint,
    presentIllness,
    pastIllness,
    personalHistory,
    familyHistory,
    sensitiveNote,
  });


  const withEdits = (rows: ExamRow[]): ExamRow[] =>
    rows.map((row) => ({
      ...row,
      result: pe[row.key] ?? row.result,
      description: peText[row.key] ?? row.description,
    }));

  const examRowPayload = (): ExamRow[] => withEdits(apiRows ?? []);
  const templateRowPayload = (): ExamRow[] =>
    withEdits(selectedTemplateRows(apiTemplateRows, selectedTemplates));

  /** บันทึกทุกส่วนใน action เดียว ผลอ่านจาก slice.recordSave (ดู ExamV4.SaveRecord) */
  const dispatchRecordSave = (
    mode: RecordSaveMode,
    approval?: { username: string; password: string },
  ): void => {
    const requestId = `${Date.now()}`;
    pendingSaveRef.current = requestId;
    dispatch({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SAVE_RECORD,
      card: cardName,
      ...sliceIds,
      requestId,
      mode,
      subjective: subjectivePayload(),
      rows: examRowPayload(),
      templateRows: templateRowPayload(),
      other: { ...(examSlice?.other ?? { id: null, emr: null }), otherDetail: peOther },
      diagnosis: { principal: principalRow, secondary },
      currentUserId,
      approvedByUsername: approval?.username,
      approvedByPassword: approval?.password,
      /*ExamV4 ใช้ตัดสิน auto check-in IN_QUEUE -> IN_EXAM หลังบันทึก (V3) */
      currentStatus: status,
    });
  };

  const dispatchHistorySave = (): void =>
    dispatch({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SAVE_HISTORY,
      card: cardName,
      ...sliceIds,
      subjective: subjectivePayload(),
    });

  /**
   * V3 กด Save ครั้งเดียวแล้วบันทึก 2 อย่างพร้อมกัน
   * (putPatientHistoryEMR + post/putSensitiveNoteEMR ใน Promise.all: SubPatientHistory.tsx:183-210)
   */
  const dispatchSensitiveNoteSave = (): void =>
    dispatch({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SAVE_SENSITIVE_NOTE,
      card: cardName,
      ...sliceIds,
      sensitiveNote,
    });

  const sensitiveNoteRecord = examSlice?.sensitiveNoteRecord ?? SensitiveNoteRecordInitial;
  /** V3 ทาสีปุ่ม History เมื่อมีเนื้อหาในประวัติหรือ sensitive note */
  const historyHasContent = hasHistoryContent(
    { pastIllness, personalHistory, familyHistory, sensitiveNote },
    sensitiveNoteRecord,
  );

  const openHistoryModal = (): void => {
    setHistoryOpen(true);
    /* V3 เรียก getSensitiveNoteEMR ทุกครั้งที่เปิด modal (SubPatientHistory.tsx:139-143) */
    dispatch({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD_SENSITIVE_NOTE,
      card: cardName,
      ...sliceIds,
    });
  };

  const saveHistoryModal = (): void => {
    dispatchHistorySave();
    dispatchSensitiveNoteSave();
    setHistoryOpen(false);
  };

  const handleSaveDraft = (): void => {
    if (mockMode) {
      setMockSavedAt(timeOfNow());
      return;
    }
    dispatchRecordSave("draft");
  };

  const handleConfirm = (): void => {
    if (confirmDisabled || principalRow === null) return;

    if (mockMode) {
      setConfirmed(true);
      setMockSavedAt(timeOfNow());
      onConfirm?.({ code: principalRow.icdCode, term: principalRow.medicalDescription });
      return;
    }

    /* V3: ผู้ใช้ที่เป็นนักศึกษา ต้องผ่าน modal ยืนยันการตรวจสอบก่อน (CardICD10.tsx:428-436) */
    if (live && approvalRequired) {
      setApprovalOpen(true);
      return;
    }
    dispatchRecordSave("confirm");
  };

  const approveWithCredentials = (): void => {
    if (approvalCreds.username.trim() === "" || approvalCreds.password === "") return;

    dispatchRecordSave("confirm", {
      username: approvalCreds.username.trim(),
      password: approvalCreds.password,
    });
    /* ไม่เก็บรหัสผ่านค้างใน state ถ้ารหัสผิดให้กรอกใหม่ */
    setApprovalCreds({ username: "", password: "" });
    setApprovalOpen(false);
  };

  return (
    <>
      <div className="hv4-grp-tabs">
        <span className="hv4-grp-tab hv4-grp-tab-on" aria-current="page">
          ประวัติ · ตรวจร่างกาย · Dx
        </span>
        <button type="button" className="hv4-grp-tab" onClick={() => onSelectMenu("gallery")}>
          Image Gallery
        </button>
        <button type="button" className="hv4-grp-tab" onClick={() => onSelectMenu("nursing")}>
          บันทึกการพยาบาล
        </button>
      </div>

      <div className="hv4-page-head">
        <h1 className="hv4-h1">ตรวจรักษา</h1>
        <span className="hv4-saved">{savedText}</span>
      </div>

      {needsCallIn && (
        <div role="status" className="hv4-banner-call">
          <span>
            ผู้ป่วยอยู่ขั้น {encounterStatusName || "รอพบแพทย์"} · ยังไม่ได้เรียกเข้าตรวจ
            &nbsp;บันทึกจะผูกกับแพทย์ผู้ตรวจเมื่อเรียกเข้าตรวจแล้ว
          </span>
          <ButtonV4 variant="ok" size="sm" disabled={callingIn} onClick={handleCallIn}>
            {callingIn ? "กำลังเรียกเข้าตรวจ…" : "เรียกผู้ป่วยเข้าตรวจ"}
          </ButtonV4>
        </div>
      )}

      {confirmed && (
        <div role="status" className="hv4-banner-done">
          <span>
            ยืนยันการตรวจและวินิจฉัยแล้ว · ขั้นต่อไป: สั่งยา / แล็บ / หัตถการ ในเมนู "คำสั่ง"
          </span>
          <button type="button" className="hv4-link" onClick={onBackToQueue}>
            กลับหน้าคิววันนี้ →
          </button>
        </div>
      )}

      <section aria-label="สรุปจากการคัดกรอง" className="hv4-summary">
        <div className="hv4-summary-head">
          <b>{summaryTitle}</b>
          <button type="button" className="hv4-link" onClick={() => onSelectMenu("screen")}>
            ดู/แก้ไขการคัดกรอง
          </button>
        </div>
        <div className="hv4-chip-row">
          {summaryChips.map((chip) => (
            <ChipV4 key={chip.text} tone={chip.tone}>
              {chip.text}
            </ChipV4>
          ))}
        </div>
      </section>

      <div className="hv4-sec-nav">
        {SECTION_LINKS.map((section) => {
          const done = section.id === "sec-hx" ? hxDone : section.id === "sec-pe" ? peDone : dxDone;
          return (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={`hv4-sec-pill${done ? " hv4-sec-pill-ok" : ""}`}
              onClick={(event) => scrollToSection(event, section.id)}
            >
              {done ? "✓ " : ""}
              {section.label}
            </a>
          );
        })}
      </div>

      <section id="sec-hx" className="hv4-card">
        <h2 className="hv4-card-title">1 · ประวัติผู้ป่วย</h2>

        <label className="hv4-label" htmlFor="hx_cc">
          อาการนำ (Chief complaint) *
        </label>
        {/* 86624 ข้อ 1: ค่าเริ่มต้นมาจากอาการนำของการคัดกรอง (LoadExam เติมให้ใน subjective) */}
        <input
          id="hx_cc"
          className="hv4-input"
          value={chiefComplaint}
          onChange={(e) => setChiefComplaint(e.target.value)}
          required
          aria-required="true"
          aria-invalid={!chiefComplaint.trim()}
        />

        <label className="hv4-label hv4-mt-16" htmlFor="hx_pi">
          ประวัติการเจ็บป่วยปัจจุบัน (Present illness) *
        </label>
        <textarea
          id="hx_pi"
          className="hv4-input hv4-textarea hv4-textarea-lg"
          rows={4}
          placeholder="เช่น ปวดหลังมาประมาณ 3 เดือน ปวดมากขึ้นเมื่อนั่งนาน"
          value={presentIllness}
          onChange={(e) => setPresentIllness(e.target.value)}
          required
          aria-required="true"
          aria-invalid={!presentIllness.trim()}
        />

        <div className="hv4-hx-grid">
          <div>
            <label className="hv4-label" htmlFor="hx_past">
              ประวัติการเจ็บป่วยในอดีต (Past illness)
            </label>
            <textarea
              id="hx_past"
              className="hv4-input hv4-textarea hv4-textarea-sm"
              rows={3}
              value={pastIllness}
              onChange={(e) => setPastIllness(e.target.value)}
            />
          </div>
          <div>
            <label className="hv4-label" htmlFor="hx_pers">
              ประวัติส่วนตัว (Personal History)
            </label>
            <textarea
              id="hx_pers"
              className="hv4-input hv4-textarea hv4-textarea-sm"
              rows={3}
              value={personalHistory}
              onChange={(e) => setPersonalHistory(e.target.value)}
            />
          </div>
          <div>
            <label className="hv4-label" htmlFor="hx_fam">
              ประวัติครอบครัว (Family History)
            </label>
            <textarea
              id="hx_fam"
              className="hv4-input hv4-textarea hv4-textarea-sm"
              rows={3}
              value={familyHistory}
              onChange={(e) => setFamilyHistory(e.target.value)}
            />
          </div>
        </div>

        <div className="hv4-mt-12">
          <span className="hv4-note">ประวัติครั้งก่อน ๆ ดูได้ที่เมนู Patient History Viewer</span>
          {live && (
            /* V3: ปุ่ม History (CardPatientInfo-Button-History) สีน้ำเงินเมื่อมีประวัติ/sensitive note
               (CardPatientInfo.tsx:326-335, 785-791) */
            <ButtonV4
              variant="ghost"
              className={`hv4-btn-history${historyHasContent ? " hv4-btn-history-on" : ""}`}
              aria-label="CardDoctorV4-Button-History"
              onClick={openHistoryModal}
            >
              ประวัติ / บันทึกเฉพาะ (History)
            </ButtonV4>
          )}
        </div>
      </section>

      {live && historyOpen && (
        /* modal เดียวกับ V3 (apps/DPO/SubPatientHistory ที่ถูกเปิดจาก CardPatientInfo.tsx:702-716)
           field เรียงคู่: Past illness | Personal History แล้ว Family History | Sensitive Note */
        <ModalV4 title="History (ประวัติ + บันทึกเฉพาะ)" onClose={() => setHistoryOpen(false)}>
          <div className="hv4-hx-grid">
            <div>
              <label className="hv4-label" htmlFor="hx_modal_past">
                ประวัติการเจ็บป่วยในอดีต (Past illness)
              </label>
              <textarea
                id="hx_modal_past"
                className="hv4-input hv4-textarea hv4-textarea-lg"
                rows={5}
                value={pastIllness}
                onChange={(event) => setPastIllness(event.target.value)}
              />
            </div>
            <div>
              <label className="hv4-label" htmlFor="hx_modal_pers">
                ประวัติส่วนตัว (Personal History)
              </label>
              <textarea
                id="hx_modal_pers"
                className="hv4-input hv4-textarea hv4-textarea-lg"
                rows={5}
                value={personalHistory}
                onChange={(event) => setPersonalHistory(event.target.value)}
              />
            </div>
            <div>
              <label className="hv4-label" htmlFor="hx_modal_fam">
                ประวัติครอบครัว (Family History)
              </label>
              <textarea
                id="hx_modal_fam"
                className="hv4-input hv4-textarea hv4-textarea-lg"
                rows={5}
                value={familyHistory}
                onChange={(event) => setFamilyHistory(event.target.value)}
              />
            </div>
            <div>
              <label className="hv4-label" htmlFor="hx_modal_sn">
                บันทึกเฉพาะ (Sensitive Note)
              </label>
              <textarea
                id="hx_modal_sn"
                className="hv4-input hv4-textarea hv4-textarea-lg"
                rows={5}
                value={sensitiveNote}
                onChange={(event) => setSensitiveNote(event.target.value)}
              />
            </div>
          </div>

          {sensitiveNoteRecord.editUser !== "" && (
            <div className="hv4-mt-8">
              <span className="hv4-note">
                ผู้บันทึกล่าสุด: {sensitiveNoteRecord.editUser}
                {sensitiveNoteRecord.editedAt !== "" ? ` · ${sensitiveNoteRecord.editedAt}` : ""}
              </span>
            </div>
          )}

          <span className="hv4-modal-note">
            backend เก็บ record ของแต่ละแผนก (division) · แก้เฉพาะบันทึกเฉพาะของเวร/แผนกที่เปิด
          </span>

          {busy(ACTIONS.SAVE_SENSITIVE_NOTE) && (
            <AlertV4 tone="info" className="hv4-mt-8">
              กำลังบันทึกลันทึกเฉพาะ…
            </AlertV4>
          )}

          {loadError() !== null && (
            <AlertV4
              tone="danger"
              className="hv4-mt-8"
              onClose={() => setProp?.("errorMessage." + cardName, null)}
            >
              {loadError()}
            </AlertV4>
          )}

          <div className="hv4-mt-10">
            <ButtonV4
              variant="confirm"
              className="hv4-btn-history-save"
              disabled={busy(ACTIONS.SAVE_HISTORY) || busy(ACTIONS.SAVE_SENSITIVE_NOTE)}
              onClick={saveHistoryModal}
            >
              บันทึกประวัติ + บันทึกเฉพาะ
            </ButtonV4>
            <ButtonV4 variant="ghost" className="hv4-btn-history-cancel" onClick={() => setHistoryOpen(false)}>
              ยกเลิก
            </ButtonV4>
          </div>
        </ModalV4>
      )}

      <section id="sec-pe" className="hv4-card">
        <div className="hv4-pe-head">
          <div>
            <h2 className="hv4-card-title">2 · ตรวจร่างกาย (Physical Exam) *</h2>
          </div>
          {showPeTemplate && (
            <div>
              <label className="hv4-label" htmlFor="pe_tpl">
                Template
              </label>
              <SelectV4
                id="pe_tpl"
                className="hv4-inline-select"
                options={PE_TEMPLATE_OPTIONS.map((option) => ({ value: option, text: option }))}
                value={peTemplate}
                onChange={handlePeTemplate}
              />
            </div>
          )}
        </div>

        {/* 86625 ข้อ 4.1-4.2: dropdown multi-select ที่ chip ✖ ทีละอัน + ✖ ท้ายช่องล้างทั้งหมด
            รายการใน dropdown มีจำนวนรายการของ template และซ่อน template ที่เลือกแล้ว */}
        {live && templateGroups.length > 0 && (
          <div className="hv4-pe-tpl-picker" role="group" aria-label="เลือก Template การตรวจร่างกาย">
            <label className="hv4-label" htmlFor="pe_tpl_select">
              Template การตรวจร่างกาย
            </label>
            <div className="hv4-pe-tpl-field">
              <SelectV4
                id="pe_tpl_select"
                className="hv4-pe-tpl-select"
                multiple
                allChips
                hideSelectedInMenu
                placeholder={selectedTemplates.length > 0 ? "+ เพิ่ม Template" : "เลือก Template การตรวจร่างกาย"}
                aria-label="เลือก Template การตรวจร่างกาย"
                options={templateGroups.map((group) => ({
                  value: String(group.id),
                  text: `${group.name} (${group.rows.length} รายการ)`,
                }))}
                value={selectedTemplates.map((id) => String(id))}
                onChange={(next) => setSelectedTemplates(next.map((value) => Number(value)))}
                /* ไม่ disabled เมื่อเลือกครบทุก template เพราะผู้ใช้ยังต้องกด ✖ ของ chip เอาออกได้
                    (dropdown จะไม่มีรายการเหลือ + มีข้อความ "เลือกครบทุก Template แล้ว" บอกอยู่) */
              />
              {selectedTemplates.length > 0 && (
                <button
                  type="button"
                  className="hv4-pe-tpl-clear"
                  aria-label="ล้าง Template ที่เลือกทั้งหมด"
                  onClick={() => setSelectedTemplates([])}
                >
                  ✕
                </button>
              )}
            </div>
            {unselectedTemplateGroups.length === 0 && (
              <span className="hv4-pe-tpl-done">เลือกครบทุก Template แล้ว</span>
            )}
          </div>
        )}

        <div className="hv4-pe-rows">
          {peRows.map((row) => (
            <PeRowControls
              key={row.key}
              row={row}
              value={pe[row.key] ?? ""}
              text={peText[row.key] ?? ""}
              warn={abnormalOf(row)}
              onSelect={handlePeSelect}
              onText={handlePeText}
            />
          ))}
        </div>

        {/* master organ ของแผนกว่าง -> grid หายทั้งชุด ต้องบอกผู้ใช้ว่าทำไมเหลือแค่ช่องพิมพ์เอง
            (V3 เคยมีข้อความแบบนี้ใน CardPatientInfo.tsx:345-351) */}
        {live && peRows.length === 0 && (
          <AlertV4
            tone="warn"
            className="hv4-mt-10"
          >
            หน่วยงานนี้ยังไม่มีรายการตรวจร่างกายจาก master ("GET /apis/DPO/physical_exam_organ/") —
            ใช้ช่อง "ตรวจร่างกายอื่น ๆ (พิมพ์เอง)" ด้านล่าง
            แล้วแจ้งทีมระบบให้ตั้งค่ารายการตรวจร่างกายของแผนก
          </AlertV4>
        )}

        {/* 86625 ข้อ 4.3-4.4: template ที่เลือกแสดงเป็นกล่องแยก หัวกล่องพื้นฟ้าอ่อน
            กดย่อ/ขยายได้ (▼/▶) และแสดง "บันทึกแล้ว {n}/{ทั้งหมด}" */}
        {selectedTemplateGroups.map((group) => {
          const collapsed = collapsedTemplates.includes(group.id);
          const answered = answeredTemplateRows(
            group.rows,
            Object.fromEntries(group.rows.map((row) => [row.key, pe[row.key] ?? row.result])),
          );
          return (
            <div key={group.id} className="hv4-pe-tpl-box">
              <button
                type="button"
                className="hv4-pe-tpl-head"
                aria-expanded={!collapsed}
                aria-controls={`hv4-tpl-${group.id}`}
                onClick={() => toggleTemplateCollapsed(group.id)}
              >
                <span className="hv4-pe-tpl-caret" aria-hidden="true">
                  {collapsed ? "▶" : "▼"}
                </span>
                <b className="hv4-pe-tpl-name">{group.name}</b>
                <span className="hv4-pe-tpl-count">
                  บันทึกแล้ว {answered}/{group.rows.length}
                </span>
              </button>
              {!collapsed && (
                <div id={`hv4-tpl-${group.id}`} className="hv4-pe-rows">
                  {group.rows.map((row) => (
                    <PeRowControls
                      key={row.key}
                      row={templateRowView(row)}
                      value={pe[row.key] ?? row.result}
                      text={peText[row.key] ?? row.description}
                      warn={abnormalOf(
                        { key: row.key, label: row.organName, options: [], placeholder: "" },
                        apiTemplateRows,
                      )}
                      onSelect={handlePeSelect}
                      onText={handlePeText}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <label className="hv4-label hv4-mt-14" htmlFor="pe_other">
          ตรวจร่างกายอื่น ๆ (พิมพ์เอง)
        </label>
        <textarea
          id="pe_other"
          className="hv4-input hv4-textarea"
          rows={2}
          placeholder="ระบุข้อมูลการตรวจร่างกายอื่น ๆ"
          value={peOther}
          onChange={(e) => setPeOther(e.target.value)}
        />
      </section>

      <section id="sec-dx" className="hv4-card">
        <h2 className="hv4-card-title">3 · วินิจฉัย (Diagnosis) *</h2>

        <div className="hv4-dx-box">
          <b className="hv4-dx-title">Principal</b>

          <div className="hv4-mt-10" ref={principalSearchRef}>
            <label className="hv4-label" htmlFor="dx_qp">
              ค้นหา ICD-10
            </label>
            <input
              id="dx_qp"
              className="hv4-input"
              placeholder="พิมพ์ เช่น M54 หรือ ปวดหลัง"
              value={principalQuery}
              autoComplete="off"
              onChange={(e) => {
                setPrincipalQuery(e.target.value);
                searchIcd("principal", e.target.value);
              }}
              onKeyDown={(e) => handleIcdKeyDown("principal", e)}
            />
            {principalResults.length > 0 && (
              <div className="hv4-icd-list">
                {principalResults.map((entry, index) => (
                  <button
                    key={entry.code}
                    type="button"
                    className={`hv4-icd-item${principalActiveIndex === index ? " hv4-icd-active" : ""}`}
                    onClick={() => pickPrincipal(entry)}
                  >
                    <b className="hv4-icd-code">{entry.code}</b>
                    <span>{entry.term}</span>
                  </button>
                ))}
              </div>
            )}
            {principalSearchOpen && principalResults.length === 0 && (
              <div className="hv4-icd-empty">ไม่พบรายการ — ลองพิมพ์รหัสหรือคำอื่น</div>
            )}
          </div>

          <div className="hv4-grid hv4-mt-14">
            <div>
              <label className="hv4-label" htmlFor="dx_mt">
                Medical Term (คำวินิจฉัยของแพทย์)
              </label>
              <input
                id="dx_mt"
                className="hv4-input"
                value={principalTerm}
                onChange={(e) => setPrincipalTerm(e.target.value)}
              />
            </div>
            <div>
              <label className="hv4-label" htmlFor="dx_pt">
                ICD10 Term
              </label>
              <input
                id="dx_pt"
                className="hv4-input"
                readOnly
                value={principal === null ? "" : principal.icdTerm}
              />
            </div>
            <div>
              <label className="hv4-label" htmlFor="dx_pc">
                ICD10 Code
              </label>
              <input
                id="dx_pc"
                className="hv4-input"
                readOnly
                value={principal === null ? "" : principal.icdCode}
              />
            </div>
          </div>

          <div className="hv4-dx-flag-row">
            <label className="hv4-check-label" htmlFor="dx_active">
              <input
                id="dx_active"
                type="checkbox"
                className="hv4-check-box"
                checked={principalActive}
                onChange={(e) => setPrincipalActive(e.target.checked)}
              />
              Active disease (โรคที่ยังเป็นอยู่/เรื้อรัง)
            </label>
            <ButtonV4 variant="ghost" onClick={clearPrincipal}>
              ล้าง Principal
            </ButtonV4>
          </div>

          <label className="hv4-label hv4-mt-8" htmlFor="dx_cause">
            สาเหตุเพิ่มเติม
          </label>
          <textarea
            id="dx_cause"
            className="hv4-input hv4-textarea"
            rows={2}
            value={principalCause}
            onChange={(e) => setPrincipalCause(e.target.value)}
          />
        </div>

        <div className="hv4-dx-box hv4-mt-14">
          <b className="hv4-dx-title">Secondary (ถ้ามี)</b>

          <div className="hv4-sec-list">
            {secondary.map((row) => (
              <div key={`${row.icdCode}-${row.pk ?? row.icd10}`} className="hv4-sec-item">
                <span>
                  <b>{row.icdCode}</b> · {row.medicalDescription || row.icdTerm}
                </span>
                <button
                  type="button"
                  className="hv4-sec-del"
                  aria-label="ลบ Secondary"
                  onClick={() => removeSecondary(row.icdCode)}
                >
                  ลบ
                </button>
              </div>
            ))}
          </div>

          <div className="hv4-mt-10" ref={secondarySearchRef}>
            <label className="hv4-label" htmlFor="dx_qs">
              เพิ่ม Secondary — ค้นหา ICD-10
            </label>
            <input
              id="dx_qs"
              className="hv4-input"
              placeholder="พิมพ์รหัสหรือชื่อโรค"
              value={secondaryQuery}
              autoComplete="off"
              onChange={(e) => {
                setSecondaryQuery(e.target.value);
                searchIcd("secondary", e.target.value);
              }}
              onKeyDown={(e) => handleIcdKeyDown("secondary", e)}
            />
            {secondaryResults.length > 0 && (
              <div className="hv4-icd-list">
                {secondaryResults.map((entry, index) => (
                  <button
                    key={entry.code}
                    type="button"
                    className={`hv4-icd-item${secondaryActiveIndex === index ? " hv4-icd-active" : ""}`}
                    onClick={() => pickSecondary(entry)}
                  >
                    <b className="hv4-icd-code">{entry.code}</b>
                    <span>{entry.term}</span>
                  </button>
                ))}
              </div>
            )}
            {secondarySearchOpen && secondaryResults.length === 0 && (
              <div className="hv4-icd-empty">ไม่พบรายการ — ลองพิมพ์รหัสหรือคำอื่น</div>
            )}
          </div>
        </div>
      </section>

      <div className="hv4-footer">
        {/* ข้อความ loading/error วางเหนือปุ่ม บันทึกร่าง / Confirm */}
        {live && busy(ACTIONS.LOAD) && (
          <AlertV4 tone="info">
            กำลังโหลดข้อมูลเวชระเบียน…
          </AlertV4>
        )}

        {live && savingRecord && <AlertV4 tone="info">กำลังบันทึกเวชระเบียน…</AlertV4>}

        {!mockMode && loadError() !== null && (
          <AlertV4
            tone="danger"
            onClose={() => setProp?.("errorMessage." + cardName, null)}
          >
            {loadError()}
          </AlertV4>
        )}
        <span
          className={`hv4-footer-msg ${
            missing.length > 0 ? "hv4-footer-msg-missing" : "hv4-footer-msg-ok"
          }`}
        >
          {missText}
        </span>

        {confirmed && (
          /* design ใช้ข้อความ "ยืนยันแล้ว" ใน footer — เพิ่มคำแนะนำเฉพาะกรณียืนยันแล้ว
             (ตอนแรก layout เท่ากับแบบเป๊ะ ๆ) */
          <span className="hv4-footer-msg hv4-footer-msg-again">
            ยืนยันครั้งเดียวต่อรอบ · แก้ไขแล้วกด "บันทึกร่าง" เพื่อ updateเวชระเบียน
          </span>
        )}
        <ButtonV4
          variant="ghost"
          className="hv4-btn-save-draft"
          disabled={savingRecord}
          onClick={handleSaveDraft}
        >
          บันทึกร่าง
        </ButtonV4>
        <ButtonV4 variant="confirm" disabled={confirmDisabled} onClick={handleConfirm}>
          ยืนยันการตรวจ (Confirm)
        </ButtonV4>

        {/* live mode เท่านั้น: flag การอนุมัติวินิจฉัย (backend มี need_approve/student_user/approved_by) */}
        {live && diagnosisApproval.approvedByName !== "" && (
          <span className="hv4-footer-msg hv4-footer-msg-ok">
            อนุมัติโดย {diagnosisApproval.approvedByName}
          </span>
        )}
        {live && approvalRequired && (
          <span className="hv4-footer-msg hv4-footer-msg-missing">
            {diagnosisApproval.studentName !== ""
              ? `บันทึกของนักศึกษา ${diagnosisApproval.studentName} ต้องมีการอนุมัติโดยแพทย์ผู้ดูแล`
              : "บันทึกนี้ต้องมีการอนุมัติโดยแพทย์ผู้ดูแล"}
          </span>
        )}
      </div>

      {live && approvalOpen && (
        <ModalV4
          title="ยืนยันการตรวจสอบ (อนุมัติโดยแพทย์ผู้ดูแล)"
          onClose={() => setApprovalOpen(false)}
        >
          <div className="hv4-md-cred-row">
            <label className="hv4-label" htmlFor="dx_approve_user">
              Username (แพทย์ผู้ดูแล) *
            </label>
            <input
              id="dx_approve_user"
              className="hv4-input"
              autoComplete="off"
              value={approvalCreds.username}
              onChange={(event) =>
                setApprovalCreds((current) => ({ ...current, username: event.target.value }))
              }
            />
            <label className="hv4-label" htmlFor="dx_approve_pass">
              Password (แพทย์ผู้ดูแล) *
            </label>
            <input
              id="dx_approve_pass"
              type="password"
              className="hv4-input"
              autoComplete="off"
              value={approvalCreds.password}
              onChange={(event) =>
                setApprovalCreds((current) => ({ ...current, password: event.target.value }))
              }
            />
          </div>
          <div className="hv4-modal-note">
            V3 ทำแบบเดียวกัน: ผู้ใช้ที่เป็นนักศึกษาต้องพิมพ์ชื่อผู้ใช้/รหัสผ่านของแพทย์ผู้ดูแลก่อนบันทึก
            (apps/DPO/CardICD10.tsx ModAuthen) backend เก็บเป็น approved_by_username/approved_by_password
          </div>
          <div className="hv4-mt-8">
            <ButtonV4
              variant="confirm"
              disabled={
                approvalCreds.username.trim() === "" ||
                approvalCreds.password === "" ||
                savingRecord
              }
              onClick={approveWithCredentials}
            >
              อนุมัติและยืนยันการตรวจ
            </ButtonV4>
            <ButtonV4 variant="ghost" onClick={() => setApprovalOpen(false)}>
              ยกเลิก
            </ButtonV4>
          </div>
        </ModalV4>
      )}
    </>
  );
};

export default CardDoctorV4;
